import { useMutation, useQuery } from '@tanstack/react-query';

import performanceScoreLevelService, {
    type PerformanceScoreLevelReplacePayload,
} from './performanceScoreLevelService';

import type {
    PerformanceScoreLevel,
    PerformanceScoreLevelDeletionImpact,
} from '@/types/competency/PerformanceScoreLevel';

import { queryClient } from '@/AppWithProviders';
import { PerformanceScoreLevelQueryKey } from '@/constants/QueryKeys';

const invalidatePerformanceScoreLevels = () => {
    queryClient.invalidateQueries({
        queryKey: [PerformanceScoreLevelQueryKey.PERFORMANCE_SCORE_LEVELS],
    });
};

export const useGetPerformanceScoreLevels = (enabled = true) => {
    return useQuery<PerformanceScoreLevel[]>({
        queryKey: [PerformanceScoreLevelQueryKey.PERFORMANCE_SCORE_LEVELS],
        queryFn: () => performanceScoreLevelService.getPerformanceScoreLevels(),
        enabled,
    });
};

export const useReplacePerformanceScoreLevels = () => {
    return useMutation<PerformanceScoreLevel[], Error, PerformanceScoreLevelReplacePayload>({
        mutationFn: (payload) =>
            performanceScoreLevelService.replacePerformanceScoreLevels(payload),
        onSuccess: invalidatePerformanceScoreLevels,
    });
};

export const useDeletePerformanceScoreLevel = () => {
    return useMutation<PerformanceScoreLevel, Error, { id: string; replacementLevelId?: string }>({
        mutationFn: ({ id, replacementLevelId }) =>
            performanceScoreLevelService.deletePerformanceScoreLevel(id, replacementLevelId),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: [PerformanceScoreLevelQueryKey.PERFORMANCE_SCORE_LEVELS],
            });
        },
    });
};

export const useGetPerformanceScoreLevelDeletionImpact = (id: string | null | undefined) => {
    return useQuery<PerformanceScoreLevelDeletionImpact, Error>({
        queryKey: [PerformanceScoreLevelQueryKey.PERFORMANCE_SCORE_LEVEL_DELETION_IMPACT, id],
        queryFn: () => performanceScoreLevelService.getPerformanceScoreLevelDeletionImpact(id!),
        enabled: Boolean(id),
    });
};
